// Summary mode, HTML cards only. no circular layout.
//
// layout: three columns at md+ (subnets | top risky hosts | engagement
// totals), collapses to a stack on narrow screens. subnet cards drive
// drawer open via the kind:'subnet' payload (reused from the existing
// drawer). host cards open the standard host drawer payload

import React, { useState } from 'react'
import { AlertTriangle, ChevronDown, ChevronRight, Network, Server, Shield } from 'lucide-react'
import type {
  EngagementAsset,
  EngagementAttackRun,
  EngagementFinding,
  EngagementPort,
  EngagementSubnet,
} from '@/lib/engagementsApi'
import { aggregate, classifySubnets, RISK_TONES, type HostAggregate, type SubnetAggregate } from './topologyAggregates'

interface Props {
  assets:    EngagementAsset[]
  subnets:   EngagementSubnet[]
  ports:     EngagementPort[]
  attackRuns: EngagementAttackRun[]
  findings:  EngagementFinding[]
  onSelect:  (data: Record<string, any> | null) => void
}

export const SummaryCards: React.FC<Props> = ({ assets, subnets, ports, attackRuns, findings, onSelect }) => {
  const { subnets: subnetAggs, totals } = aggregate(assets, subnets, ports, attackRuns, findings)
  // hide /32 route artifacts (own-IP, broadcast) from the headline view,
  // keep them under a collapsible debug section
  const { display: displaySubnets, ignored: ignoredSubnets } = classifySubnets(subnetAggs)
  const [showIgnored, setShowIgnored] = useState(false)

  // ── Hosts with findings ────────────────────────────────────────────
  // only hosts that carry at least one Finding or a HIGH/CRITICAL port
  // severity belong here. attack runs alone DO NOT promote a host into
  // this list, they appear in the separate "Attack simulation targets"
  // section so the user can tell "we tested it" apart from "we found
  // something wrong"
  const allHosts  = displaySubnets.flatMap(s => s.hosts)
  const withFindings = allHosts
    .filter(h => h.findings.total > 0 || h.highSevPorts > 0)
    .sort((a, b) => {
      const ra = riskOrder(a.topRisk), rb = riskOrder(b.topRisk)
      if (ra !== rb) return ra - rb
      if (b.findings.total !== a.findings.total) return b.findings.total - a.findings.total
      return b.highSevPorts - a.highSevPorts
    })
    .slice(0, 8)

  // ── Attack simulation targets ──────────────────────────────────────
  // hosts that were attacked. we don't classify these as "risky", the
  // user explicitly aimed traffic at them. impact is undetermined
  // unless a finding came back. hosts that ALSO have findings appear
  // in the Hosts-with-findings list, not here, to avoid double display
  const withFindingIps = new Set(withFindings.map(h => h.asset.host_ip))
  const attackTargets = allHosts
    .filter(h => h.attackCount > 0 && !withFindingIps.has(h.asset.host_ip))
    .sort((a, b) => b.attackCount - a.attackCount)
    .slice(0, 8)

  return (
    <div className="h-full overflow-auto bg-slate-50 dark:bg-slate-900/40 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
      <div className="grid gap-4 lg:grid-cols-[1.4fr,1fr,0.9fr] grid-cols-1">

        {/* Subnets column */}
        <section>
          <SectionHeader
            icon={<Network className="h-3.5 w-3.5" />}
            label={`Subnets (${displaySubnets.length})`}
            hint={ignoredSubnets.length > 0
              ? `${displaySubnets.length} display, ${ignoredSubnets.length} ignored route artifact${ignoredSubnets.length === 1 ? '' : 's'}`
              : undefined}
          />
          {displaySubnets.length === 0 ? (
            <EmptyHint text="No subnets discovered." />
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {displaySubnets.map(s => (
                <SubnetCard key={s.cidr} s={s} onClick={() => onSelect(buildSubnetPayload(s))} />
              ))}
            </div>
          )}
          {ignoredSubnets.length > 0 && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setShowIgnored(v => !v)}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-400 hover:text-slate-600"
                title="Auto-route /32 entries that don't represent real subnets"
              >
                {showIgnored ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                Ignored route artifacts ({ignoredSubnets.length})
              </button>
              {showIgnored && (
                <div className="grid sm:grid-cols-2 gap-2 mt-2 opacity-70">
                  {ignoredSubnets.map(s => (
                    <button
                      key={s.cidr}
                      type="button"
                      onClick={() => onSelect(buildSubnetPayload(s))}
                      className="text-left bg-slate-50 dark:bg-slate-900/30 border border-dashed border-slate-300 dark:border-slate-700 rounded p-2"
                      title={`Excluded /32 (${s.discovery_source ?? 'auto'})`}
                    >
                      <div className="font-mono text-xs text-slate-500">{s.cidr}</div>
                      <div className="text-[10px] text-slate-400">{s.discovery_source ?? 'auto-route'} · {s.hostCount} host{s.hostCount === 1 ? '' : 's'}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        {/* hosts with findings + attack-simulation targets, stacked */}
        <section className="flex flex-col gap-4">
          <div>
            <SectionHeader icon={<AlertTriangle className="h-3.5 w-3.5" />}
                           label={`Hosts with findings (${withFindings.length})`} />
            {withFindings.length === 0 ? (
              <EmptyHint text="No confirmed findings yet." />
            ) : (
              <div className="flex flex-col gap-2">
                {withFindings.map(h => (
                  <HostRiskCard key={h.asset.host_ip} h={h} onClick={() => onSelect(buildHostPayload(h))} />
                ))}
              </div>
            )}
          </div>

          {attackTargets.length > 0 && (
            <div>
              <SectionHeader icon={<AlertTriangle className="h-3.5 w-3.5" />}
                             label={`Attack simulation targets (${attackTargets.length})`}
                             hint="hosts that received attack simulations — not necessarily vulnerable" />
              <div className="flex flex-col gap-2">
                {attackTargets.map(h => (
                  <HostRiskCard key={h.asset.host_ip} h={h} onClick={() => onSelect(buildHostPayload(h))} tested />
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Totals column */}
        <section>
          <SectionHeader icon={<Shield className="h-3.5 w-3.5" />} label="Engagement totals" />
          <div className="grid grid-cols-2 gap-2">
            <Stat label="Hosts"        value={totals.hosts} />
            <Stat
              label="Subnets"
              value={displaySubnets.length}
              tooltip={ignoredSubnets.length > 0
                ? `${displaySubnets.length} display subnet, ${ignoredSubnets.length} ignored route artifact${ignoredSubnets.length === 1 ? '' : 's'}`
                : undefined}
            />
            <Stat label="Classified"            value={totals.classified} />
            <Stat label="Simulation targets"    value={totals.attacked} tone="slate"
                  tooltip="Hosts that received attack simulations. Not labelled risky." />
            <Stat label="Confirmed high-risk"   value={totals.highRisk} tone={totals.highRisk > 0 ? 'orange' : 'slate'}
                  tooltip="Hosts with at least one confirmed HIGH or CRITICAL finding." />
            <Stat label="Confirmed findings"    value={totals.findings.total} />
          </div>

          {totals.findings.total > 0 && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Severity breakdown</div>
              <SeverityBar counts={totals.findings} />
            </div>
          )}
        </section>

      </div>
    </div>
  )
}

const SectionHeader: React.FC<{ icon: React.ReactNode; label: string; hint?: string }> = ({ icon, label, hint }) => (
  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-500 mb-2 font-semibold" title={hint}>
    {icon}
    {label}
    {hint && <span className="ml-1 text-slate-400 normal-case font-normal">· {hint}</span>}
  </div>
)

const SubnetCard: React.FC<{ s: SubnetAggregate; onClick: () => void }> = ({ s, onClick }) => {
  const tone = RISK_TONES[s.worstSeverity]
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left bg-white dark:bg-slate-800 border-2 ${tone.border} rounded-lg p-3 hover:shadow-md transition-shadow`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="font-mono text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{s.cidr}</span>
        <span className={`inline-block w-2 h-2 rounded-full ${tone.dot}`} />
      </div>
      <div className="text-[11px] text-slate-600 dark:text-slate-300">
        {s.hostCount} host{s.hostCount === 1 ? '' : 's'}
        {s.classifiedCount > 0 && <span className="text-slate-400"> · {s.classifiedCount} classified</span>}
      </div>
      <div className="flex flex-wrap gap-1 mt-2">
        {s.attackedCount > 0 && <Chip tone="rose">{s.attackedCount} attacked</Chip>}
        {s.highRiskCount > 0 && <Chip tone="orange">{s.highRiskCount} high-risk</Chip>}
        {s.findingCount > 0   && <Chip tone="slate">{s.findingCount} finding{s.findingCount === 1 ? '' : 's'}</Chip>}
      </div>
    </button>
  )
}

const HostRiskCard: React.FC<{ h: HostAggregate; onClick: () => void; tested?: boolean }> = ({ h, onClick, tested }) => {
  // tested-only hosts use a neutral tone so the UI doesn't imply
  // they're inherently risky, they were targets, not necessarily
  // compromised
  const tone = tested ? RISK_TONES.normal : RISK_TONES[h.topRisk]
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left bg-white dark:bg-slate-800 border-l-4 ${tone.border} rounded-md p-2.5 hover:shadow-md transition-shadow`}
    >
      <div className="flex items-center gap-2">
        <Server className="h-3.5 w-3.5 text-slate-400" />
        <span className="font-mono text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">
          {h.asset.host_name || h.asset.host_ip}
        </span>
      </div>
      <div className="text-[10px] text-slate-500 font-mono mt-0.5">{h.asset.host_ip}</div>
      <div className="flex flex-wrap gap-1 mt-1.5">
        {/* attack simulation chip, purely factual, no risk colour */}
        {tested && h.attackCount > 0 && (
          <Chip tone="slate">{h.attackCount} simulation run{h.attackCount === 1 ? '' : 's'}</Chip>
        )}
        {!tested && h.attackCount > 0 && (
          <Chip tone="slate">{h.attackCount} simulation run{h.attackCount === 1 ? '' : 's'}</Chip>
        )}
        {h.findings.critical > 0 && <Chip tone="rose">{h.findings.critical} crit</Chip>}
        {h.findings.high > 0     && <Chip tone="orange">{h.findings.high} high</Chip>}
        {h.findings.medium > 0   && <Chip tone="amber">{h.findings.medium} med</Chip>}
      </div>
    </button>
  )
}

const Stat: React.FC<{ label: string; value: number; tone?: 'slate' | 'rose' | 'orange' | 'amber'; tooltip?: string }> = ({ label, value, tone = 'slate', tooltip }) => {
  const toneCls = tone === 'rose' ? 'text-rose-600' : tone === 'orange' ? 'text-orange-600' : 'text-slate-800 dark:text-slate-100'
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md p-2.5" title={tooltip}>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${toneCls}`}>{value}</div>
    </div>
  )
}

const SeverityBar: React.FC<{ counts: { critical: number; high: number; medium: number; low: number; info: number; total: number } }> = ({ counts }) => {
  const total = Math.max(1, counts.total)
  const segs: { tone: keyof typeof RISK_TONES; n: number; label: string }[] = [
    { tone: 'critical', n: counts.critical, label: 'C' },
    { tone: 'high',     n: counts.high,     label: 'H' },
    { tone: 'medium',   n: counts.medium,   label: 'M' },
    { tone: 'low',      n: counts.low,      label: 'L' },
    { tone: 'info',     n: counts.info,     label: 'I' },
  ]
  return (
    <div>
      <div className="flex h-2.5 w-full overflow-hidden rounded">
        {segs.map(s => s.n > 0 && (
          <div
            key={s.tone}
            className={RISK_TONES[s.tone].dot}
            style={{ width: `${(s.n / total) * 100}%` }}
            title={`${s.label}: ${s.n}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[10px] text-slate-500">
        {segs.map(s => s.n > 0 && <span key={s.tone}><b>{s.n}</b> {s.tone}</span>)}
      </div>
    </div>
  )
}

const Chip: React.FC<{ tone: 'rose' | 'orange' | 'amber' | 'slate'; children: React.ReactNode }> = ({ tone, children }) => {
  const map = {
    rose:   'bg-rose-100 text-rose-700',
    orange: 'bg-orange-100 text-orange-700',
    amber:  'bg-amber-100 text-amber-700',
    slate:  'bg-slate-100 text-slate-600',
  }
  return <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded ${map[tone]}`}>{children}</span>
}

const EmptyHint: React.FC<{ text: string }> = ({ text }) => (
  <div className="text-xs text-slate-400 italic">{text}</div>
)

function riskOrder(r: HostAggregate['topRisk']): number {
  return ['critical', 'high', 'medium', 'low', 'info', 'normal'].indexOf(r)
}

function buildSubnetPayload(s: SubnetAggregate) {
  return {
    kind: 'subnet' as const,
    cidr: s.cidr,
    hostCount: s.hostCount,
    attackedCount: s.attackedCount,
    highSevCount: s.highRiskCount,
    mediumSevCount: 0,
    hostIps: s.hosts.map(h => h.asset.host_ip),
  }
}

function buildHostPayload(h: HostAggregate) {
  const a = h.asset
  return {
    type: 'Host',
    ip: a.host_ip,
    hostname: a.host_name,
    mac: a.mac_address,
    vendor: a.vendor,
    subnet: a.subnet_cidr,
    deviceType: a.device_type,
    deviceTypeConfidence: a.device_type_confidence,
    deviceVendor: a.device_vendor,
    deviceModel: a.device_model,
    deviceFirmwareVersion: a.device_firmware_version,
    asset_id: a.id,
  }
}
